import { A } from '@ember/array';
import EmberObject, { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Mixin from '@ember/object/mixin';
import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import ImageThumbnailGenerator from '@uppy/thumbnail-generator';
import VideoThumbnailGenerator from '../utils/uppy-video-thumbnail-generator';
import splitFilename from 'sh-www/utils/split-filename';
import { showModal } from 'sh-www/utils/modal-controls';
import {
  MAX_FILE_SIZE_IN_B,
  MAX_FILE_SIZE_IN_GB,
  MAX_ASSETS_PER_MESSAGE,
  UPLOAD_COMPLETE_PERCENTAGE,
} from '../constants/assets';

/**
 * The mixin which adds asset upload management capabilities to objects.
 *
 * Overview of the upload process:
 *
 * 1. User selects file.
 * 2. Upload preparation begins automatically due to Uppy's autoProceed config setting.
 * 3. getUploadParameters() gets the upload URL from a newly created draft asset.
 * 4. File upload begins.
 * 5. Progress events fire intermittently.
 * 6. File upload finishes.
 * 7. API is polled for the asset until it exits draft state.
 * 8. Processing finishes.
 * 9. File upload is marked complete.
 *
 * Important terminology:
 *
 * - uppyID: The ID assigned by Uppy.
 * - uploadID: The ID assigned by the Asset Upload Manager.
 * - assetID: The ID assigned by the server.
 *
 * The uploadID is a base-64 encoded version of the uppyID. This is necessary because Uppy generates
 * IDs using the asset's MIME type, which may contain periods, and Ember does not support periods in
 * object keys due to the way get() and set() use strings.
 */
export default Mixin.create({
  /**
   * Register services.
   */
  errorTracker: service('error-tracker'),
  shData: service(),
  api: service(),

  /**
   * @type {Uppy} The instance of Uppy (a file uploading library).
   */
  _uppy: null,

  /**
   * @type {Ember.Object} An Ember object which receives updates so that it can emulate Uppy's
   * internal store of files. Whenever Uppy updates its files, there should be an event listener
   * that triggers this object to update accordingly. With that said, this object is not identical
   * to Uppy's version. We only keep a subset of the data for simplicity, and some data is modified
   * for display purposes (e.g. the progress percentage).
   */
  uploads: null,

  /**
   * @type {String[]} The uploadIDs of all uploads.
   */
  uploadList: null,

  /**
   * @type {String[]} The uploadIDs of uploads that are uploading or processing.
   */
  uploadPendingList: null,

  /**
   * @type {String[]} The uploadIDs of uploads that are complete.
   */
  uploadCompletedList: null,

  /**
   * @type {Boolean} Whether there are currently uploads pending.
   */
  isUploadingAssets: computed('uploadPendingList.[]', function () {
    return this.get('uploadPendingList.length');
  }),

  /**
   * @type {Boolean} Whether more assets can be added without exceeding the limit.
   */
  canAddMoreAssets: computed('uploadList.[]', function () {
    return this.get('uploadList.length') < MAX_ASSETS_PER_MESSAGE;
  }),

  /**
   * @inheritDoc
   */
  init() {
    this._super(...arguments);

    // Configure Uppy.
    const uppy = new Uppy({
      autoProceed: true,
      restrictions: {
        maxFileSize: MAX_FILE_SIZE_IN_B,
      },
    });

    // Configure the Image Thumbnail Generator plugin.
    uppy.use(ImageThumbnailGenerator, { thumbnailWidth: 80 });

    // Configure the Video Thumbnail Generator plugin.
    uppy.use(VideoThumbnailGenerator, { thumbnailWidth: 80 });

    // Configure the AWS S3 plugin.
    uppy.use(AwsS3, {
      getUploadParameters: (file) => {
        return this._createAsset(file)
          .then((response) => ({
            url: response.data[0].attributes['upload-url'],
            method: 'PUT',
          }))
          .catch(() => {
            showModal(this, 'confirmation', {
              width: 'medium',
              titleText: 'Failed to initiate file upload',
              bodyText: 'Please try again later.',
              actionButtonText: 'Ok',
            });

            this.removeFile(file.uploadID);
          });
      },
    });

    // Configure Uppy event handling.
    this._addUppyEventListeners(uppy);

    // Initialize props within init() so that each object that uses this mixin gets its own props.
    this.set('_uppy', uppy);
    this.set('uploads', EmberObject.create({}));
    this.set('uploadList', A());
    this.set('uploadPendingList', A());
    this.set('uploadCompletedList', A());
  },

  /**
   * Sets up the handlers for Uppy events.
   */
  _addUppyEventListeners(uppy) {
    uppy.on('file-added', (file) => {
      const uploadID = btoa(file.id);
      const { name, extension } = splitFilename(file.name);
      const data = {
        uppyID: file.id,
        uploadID,
        assetID: null,
        asset: null,
        type: file.type,
        filename: file.name,
        name,
        extension,
        progress: 0,
        thumbnail: null,
        status: null,
        pollTimeout: null,
      };

      this.get('uploads').set(uploadID, data);
      this.get('uploadList').pushObject(uploadID);
      this.get('uploadPendingList').pushObject(uploadID);
      uppy.setFileState(file.id, { uploadID });
    });

    uppy.on('file-removed', (file) => {
      // Remove upload from lists.
      this.get('uploadList').removeObject(file.uploadID);
      this.get('uploadPendingList').removeObject(file.uploadID);
      this.get('uploadCompletedList').removeObject(file.uploadID);

      // Clear poll timeout if this upload is being processed.
      const pollTimeout = this.get(`uploads.${file.uploadID}.pollTimeout`);
      clearTimeout(pollTimeout);

      // Delete the upload object.
      delete this.get('uploads')[file.uploadID];
      this.notifyPropertyChange('uploads');
    });

    uppy.on('thumbnail:generated', (file, thumbnail) => {
      this.set(`uploads.${file.uploadID}.thumbnail`, thumbnail);
    });

    uppy.on('video-thumbnail:generated', (file, thumbnail) => {
      this.set(`uploads.${file.uploadID}.thumbnail`, thumbnail);
    });

    uppy.on('asset-created', (file, response) => {
      this.set(`uploads.${file.uploadID}.assetID`, response.data[0].id);
      this.set(`uploads.${file.uploadID}.status`, 'draft');
      this.get('uploadCompletedList').pushObject(file.uploadID);
    });

    uppy.on('asset-processed', (uploadID, asset) => {
      this.set(`uploads.${uploadID}.asset`, asset);
      this.set(`uploads.${uploadID}.progress`, 100);

      // Finalize the process after a delay so the user has a chance to see 100% progress.
      setTimeout(() => {
        // Only continue if the upload was not deleted during the delay.
        if (!this.get(`uploads.${uploadID}`)) return;

        this.set(`uploads.${uploadID}.status`, 'active');
        this.get('uploadPendingList').removeObject(uploadID);

        // Allow objects that include this mixin to define an action that gets called when complete.
        try {
          this.onAssetFinalized(uploadID);
        } catch (error) {
          // The object has not defined this action.
        }
      }, 250);
    });

    uppy.on('upload-progress', (file, progress) => {
      // Convert the actual progress to a smaller value to leave room for processing at the end.
      const actualProgress = progress.bytesUploaded / progress.bytesTotal;
      const displayProgress = actualProgress * UPLOAD_COMPLETE_PERCENTAGE / 100;
      this.set(`uploads.${file.uploadID}.progress`, displayProgress);
    });

    uppy.on('upload-success', (file) => {
      this._pollAssetUntilProcessed(file.uploadID);
    });

    uppy.on('upload-error', (file) => {
      showModal(this, 'confirmation', {
        width: 'medium',
        titleText: 'Upload error',
        bodyText: `Failed to upload <em>${file.name}</em>. Please try again.`,
        actionButtonText: 'Ok',
        confirmAction: () => {
          this.removeFile(file.uploadID);
        },
      });
    });

    uppy.on('error', () => {
      showModal(this, 'confirmation', {
        width: 'medium',
        titleText: 'Upload error',
        bodyText: 'Please try again.',
        actionButtonText: 'Ok',
        confirmAction: () => {
          this.resetAssetUploadManager();
        },
      });
    });
  },

  /**
   * Adds files to Uppy's store.
   *
   * @param {FileList} files The files selected via a file input element.
   */
  addFiles(files, modalOptions = { }) {
    const errors = [];

    for (const file of files) {
      // Only proceed if adding this file won't exceed the limit.
      if (this.get('uploadList.length') + 1 > MAX_ASSETS_PER_MESSAGE) {
        errors.push({
          filename: file.name,
          message: `could not be uploaded because you've reached the file limit per message (${MAX_ASSETS_PER_MESSAGE})`,
        });

        continue;
      }

      try {
        this.get('_uppy').addFile({
          source: 'file input',
          name: file.name,
          type: file.type,
          data: file,
        });
      } catch (error) {
        const message = this.get('_getInvalidSelectionErrorMessage')(error.message);

        // If this is not a validation error, but rather a code-level error, log it.
        if (!message) {
          console.error(error);
          const description = `Asset Upload Manager: Could not add file '${file.name}' to Uppy`;
          this.get('errorTracker').error(description, this);

          errors.push({
            filename: file.name,
            message: 'could not be uploaded due to an error',
          });

          continue;
        }

        errors.push({
          filename: file.name,
          message: message,
        });
      }
    }

    // Display validation errors for files that could not be added.
    if (errors.length) {
      showModal(this, 'asset-invalid-selection', Object.assign({}, modalOptions, {
        errors: errors,
      }));
    }
  },

  /**
   * Removes a file from Uppy's store.
   *
   * @param {String} uploadID The uploadID of the upload to remove.
   */
  removeFile(uploadID) {
    const uppyID = this.get(`uploads.${uploadID}.uppyID`);
    this.get('_uppy').removeFile(uppyID);
  },

  /**
   * Resets Uppy to its initial state.
   */
  resetAssetUploadManager() {
    this.get('_uppy').cancelAll();
  },

  /**
   * Returns a reworded error message.
   *
   * @param {String} message The error message from Uppy.
   * @returns The custom error message to display to the user.
   */
  _getInvalidSelectionErrorMessage(message) {
    if (message.includes('This file exceeds maximum')) {
      return `is larger than ${MAX_FILE_SIZE_IN_GB} GB`;
    } else if (message.includes('You can only upload')) {
      return 'is an invalid type';
    } else if (message.includes('Cannot add the duplicate file')) {
      return 'is already added';
    }
  },

  /**
   * Creates a new asset in the backend.
   *
   * @param {String} file The Uppy file object.
   * @returns {Promise} The API response.
   */
  _createAsset(file) {
    const options = { jsonData: { file_name: file.name } };

    return this.get('api').post('/assets', options)
      .then((response) => {
        this.get('_uppy').emit('asset-created', file, response);
        return response;
      });
  },

  /**
   * Gets the final asset data from the server.
   *
   * @param {String} uploadID The uploadID of the upload.
   * @returns {Promise} The asset data from the server.
   */
  _getFinalAsset(uploadID) {
    const assetID = this.get(`uploads.${uploadID}.assetID`);
    const includes = ['uri-collection', 'uploader'];
    let options = {};

    options = this.shData.addIncludesToOptions(options, includes);

    return this.get('api').request(`/assets/${assetID}`, options)
      .then((asset) => {
        if (asset.data.attributes.status === 'draft') {
          throw new Error('Asset is a draft');
        }

        return asset;
      });
  },

  /**
   * Polls the server for an asset until it is no longer in the draft state.
   *
   * @param {String} uploadID The uploadID of the upload.
   */
  _pollAssetUntilProcessed(uploadID) {
    let attempts = 0;

    const poll = () => {
      this._getFinalAsset(uploadID)
        .then((asset) => {
          this.get('_uppy').emit('asset-processed', uploadID, asset);
        })
        .catch(() => {
          schedulePoll();
        })
        .finally(() => {
          attempts += 1;
        });
    };

    const schedulePoll = () => {
      // Only continue if the upload hasn't been removed.
      if (!this.get(`uploads.${uploadID}`)) return;

      // Set the timeout and store its ID in the upload object so we can clear it if needed.
      this.set(
        `uploads.${uploadID}.pollTimeout`,
        setTimeout(poll, this._getPollDelay(attempts)),
      );

      // Update the simulated processing progress.
      this._updateProcessingProgress(uploadID);
    };

    schedulePoll();
  },

  /**
   * Calculates the delay until the next retry based on the attempt number.
   *
   * @param {Number} attempt The attempt number.
   * @returns {Number} The time in milliseconds.
   */
  _getPollDelay(attempt) {
    return attempt ** 2 * 100;
  },

  /**
   * Updates the simulated processing progress by filling 25% of the remaining amount.
   *
   * @param {String} uploadID The uploadID of the upload.
   */
  _updateProcessingProgress(uploadID) {
    const oldDisplayProgress = this.get(`uploads.${uploadID}.progress`);
    const newDisplayProgress = oldDisplayProgress + ((1 - oldDisplayProgress) * .25);
    this.set(`uploads.${uploadID}.progress`, newDisplayProgress);
  },
});
